/**
 * Müzakere değerlendirmesi (mobil utils/assessment.ts ile aynı kurallar).
 * Soru tipleri: coktan, evethayir, dogruyanlis, acik.
 */
import { canManagerMark } from "./muzakereWeek";

export const OPEN_ANSWER_MAX = 1000;

/** Açık değerlendirme closesAt anından sonra kapalı sayılır (sunucu da okurken hesaplar). */
export function effectiveState(state, closesAt, now = Date.now()) {
  if (state !== "open" || !closesAt) return state || "none";
  const end = Date.parse(closesAt);
  return Number.isFinite(end) && now >= end ? "closed" : state;
}

/** Soruya tipine uygun bir cevap verilmiş mi. */
export function isAnswered(q, a) {
  if (!a) return false;
  if (q.type === "coktan") {
    return typeof a.choice === "number" && a.choice >= 0 && a.choice < (q.options?.length ?? 0);
  }
  if (q.type === "acik") return !!(a.text || "").trim();
  return typeof a.bool === "boolean";
}

export const allAnswered = (questions, answers) =>
  (questions || []).every((q) => isAnswered(q, answers?.[q.qid]));

/** Cevap taslağına alan yazar (tip dışındaki alanlar gönderirken atılır). */
export function patchAnswer(answers, qid, patch) {
  return { ...answers, [qid]: { ...(answers?.[qid] || { qid }), ...patch, qid } };
}

/** Gönderilecek cevaplar: soru sırasıyla, yalnız tipe ait alan. */
export function buildAnswers(questions, answers) {
  return (questions || []).map((q) => {
    const a = answers?.[q.qid] || { qid: q.qid };
    if (q.type === "coktan") return { qid: q.qid, choice: a.choice };
    if (q.type === "acik") return { qid: q.qid, text: (a.text || "").trim().slice(0, OPEN_ANSWER_MAX) };
    return { qid: q.qid, bool: a.bool };
  });
}

/** Cevap doğru mu; açık uçluda ya da doğru cevap bilinmiyorsa null. */
export function answerIsCorrect(q, a) {
  if (q.type === "acik") return null;
  if (q.type === "coktan") {
    if (typeof q.correctIndex !== "number") return null;
    return a?.choice === q.correctIndex;
  }
  if (typeof q.correctBool !== "boolean") return null;
  return a?.bool === q.correctBool;
}

/** Gönderimden sonra doğru cevaplar answerKey'den sorulara tamamlanır. */
export function withCorrectAnswers(questions, answerKey) {
  if (!answerKey?.length) return questions || [];
  const byId = new Map(answerKey.map((k) => [k.qid, k]));
  return (questions || []).map((q) => {
    const c = byId.get(q.qid);
    if (!c) return q;
    return { ...q, correctIndex: c.correctIndex ?? q.correctIndex, correctBool: c.correctBool ?? q.correctBool };
  });
}

/** Evet/Hayır ya da Doğru/Yanlış düğme metinleri (true önce). */
export const boolLabels = (type) =>
  type === "evethayir" ? ["assess_yes", "assess_no"] : ["assess_true", "assess_false"];

export function scoreText(score, t) {
  if (!score || !(score.total > 0)) return t("assess_no_scored");
  return `${score.correct}/${score.total} ${t("assess_correct_word")}`;
}

/** Verilen cevabın okunur hali; cevap yoksa boş. */
export function answerText(q, a, t) {
  if (!a) return "";
  if (q.type === "coktan") return typeof a.choice === "number" ? q.options?.[a.choice] ?? "" : "";
  if (q.type === "acik") return (a.text || "").trim();
  if (typeof a.bool !== "boolean") return "";
  const [yes, no] = boolLabels(q.type);
  return t(a.bool ? yes : no);
}

export function correctAnswerText(q, t) {
  if (q.type === "acik") return "";
  if (q.type === "coktan") return typeof q.correctIndex === "number" ? q.options?.[q.correctIndex] ?? "" : "";
  if (typeof q.correctBool !== "boolean") return "";
  const [yes, no] = boolLabels(q.type);
  return t(q.correctBool ? yes : no);
}

/** Sonuç ekranında sunucunun hükmü öncelikli; yoksa yerelde karşılaştırılır. */
export function verdictOf(q, a, answerKey) {
  const server = (answerKey || []).find((k) => k.qid === q.qid)?.isCorrect;
  return typeof server === "boolean" ? server : answerIsCorrect(q, a);
}

/* ---------- Yönetici: başlatma, soru düzenleme ve sonuçlar ---------- */

/**
 * Değerlendirme başlatılabilir mi: müzakere günü ve sonraki 7 gün (mobil
 * canStartAssessment, sunucudaki başlatma penceresi).
 */
export const canStartAssessment = (date, today) => canManagerMark(date, today);

/**
 * Var olan müzakereye soru eklenebilir ya da sorular değiştirilebilir mi:
 * değerlendirme başlamamış, başlatma süresi geçmemiş ve (sorusuzsa)
 * "Katıldım" işaretleyen üye yok (sunucu bu durumda reddeder).
 */
export function canEditQuestions(m, today) {
  const state = m?.assessment?.state ?? "none";
  if (state !== "none" && state !== "ready") return false;
  if (today >= m.date && !canManagerMark(m.date, today)) return false;
  return state === "ready" || (m.doneUserIds || []).length === 0;
}

/** Hazır değerlendirme neden başlatılamıyor; başlatılabiliyorsa null. */
export function startBlockReason(m, today) {
  if ((m?.assessment?.state ?? "none") !== "ready") return null;
  if (canStartAssessment(m.date, today)) return null;
  return today < m.date ? "notYet" : "expired";
}

/** Değerlendirmeli müzakere: üyeler kendileri cevaplar, yönetici işaretlemez. */
export const hasAssessment = (m) => (m?.assessment?.state ?? "none") !== "none";

/** Sonuçlarda kişinin cevapları qid'e göre (eski sunucuda yalnız açık cevaplar). */
export function responseAnswers(response) {
  const list = Array.isArray(response?.answers) ? response.answers : response?.openAnswers || [];
  return new Map(list.map((a) => [String(a.qid), a]));
}

/** Sunucu her gönderenin bütün cevaplarını veriyor mu (eski sunucu vermez). */
export const hasFullAnswers = (responses) =>
  (responses || []).length > 0 && (responses || []).every((r) => Array.isArray(r.answers));

/**
 * Soru bazlı istatistik: kaç kişi cevapladı, kaçı doğru; çoktan seçmelide
 * şık başına, Evet/Hayır ve Doğru/Yanlış'ta iki seçenek için sayı; açık
 * uçluda cevaplar (ad ile). Cevaplar sunucudan gelmediyse (eski sunucu)
 * yalnız açık uçlu cevaplar sayılır.
 */
export function questionStats(questions, responses) {
  const people = (responses || []).map((r) => ({
    userId: r.userId,
    name: `${r.name || ""} ${r.surname || ""}`.trim(),
    answers: responseAnswers(r),
  }));
  return (questions || []).map((q) => {
    const stat = { qid: q.qid, answered: 0, correct: 0, graded: false };
    if (q.type === "coktan") stat.counts = (q.options || []).map(() => 0);
    else if (q.type === "acik") stat.texts = [];
    else stat.counts = [0, 0]; // [true, false]
    for (const p of people) {
      const a = p.answers.get(String(q.qid));
      if (!isAnswered(q, a)) continue;
      stat.answered += 1;
      if (q.type === "coktan") stat.counts[a.choice] += 1;
      else if (q.type === "acik") stat.texts.push({ userId: p.userId, name: p.name, text: a.text.trim() });
      else stat.counts[a.bool ? 0 : 1] += 1;
      const verdict = answerIsCorrect(q, a);
      if (verdict !== null) {
        stat.graded = true;
        if (verdict) stat.correct += 1;
      }
    }
    return stat;
  });
}

/** Gönderenlerin ortalama başarısı (yüzde, tam sayı); puanlanan yoksa null. */
export function averagePercent(responses) {
  let correct = 0;
  let total = 0;
  for (const r of responses || []) {
    if (!(r?.score?.total > 0)) continue;
    correct += r.score.correct;
    total += r.score.total;
  }
  return total > 0 ? Math.round((correct / total) * 100) : null;
}

/** Oran (yüzde, tam sayı); payda sıfırsa 0. */
export const percentOf = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
